import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type ReactRendererService from 'garaje/services/react-renderer';
import type StateService from 'garaje/services/state';
import type { MatchingRules } from 'garaje/utils/matching-rule-utils';
import type { Root } from 'react-dom/client';
import type { ProfileMatchingRuleModal } from 'reactRichComponents/visitors/profiles/ProfileMatchingRuleModal';
import type { VisitorProfileShow } from 'reactRichComponents/visitors/profiles/VisitorProfileShow';

import { PREVIOUS_VISITS_PAGE_SIZE, UPCOMING_VISITS_PAGE_SIZE, type VisitorsProfilesShowModel } from './route';

type TabValue = 'visits' | 'aliases' | 'documents';

export default class VisitorsProfilesShowController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare reactRenderer: ReactRendererService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: Store;

  declare model: VisitorsProfilesShowModel;

  private modalRoot?: Root;
  // Paired so `remountReactProfileShow` can `rerender(component, root, props)`
  // without re-loading the chunk on every model swap.
  private reactProfileShowRoot?: Root;
  private reactProfileShowComponent?: typeof VisitorProfileShow;

  queryParams = ['upcomingVisitsCursor', 'previousVisitsCursor', 'from', 'id', 'tab'];
  @tracked upcomingVisitsCursor: string | null = null;
  @tracked previousVisitsCursor: string | null = null;
  @tracked from: string | null = null;
  @tracked id: string | null = null;
  @tracked tab: TabValue = 'visits';

  @tracked upcomingVisitsIsLoading: boolean = false;
  @tracked previousVisitsIsLoading: boolean = false;
  @tracked isLoadingRulesEditor: boolean = false;
  @tracked isSavingRules: boolean = false;

  get upcomingVisitsPageSize(): number {
    return UPCOMING_VISITS_PAGE_SIZE;
  }

  get previousVisitsPageSize(): number {
    return PREVIOUS_VISITS_PAGE_SIZE;
  }

  get canAccessDocumentUploads(): boolean {
    return this.abilities.can('access document-upload');
  }

  get canAccessProfileNotes(): boolean {
    return this.featureFlags.isEnabled('vis-profile-notes');
  }

  get canEditRules(): boolean {
    return this.featureFlags.isEnabled('rule-based-profile-matching');
  }

  get activeTab(): TabValue {
    if (this.tab === 'documents' && !this.canAccessDocumentUploads) {
      return 'visits';
    }
    return this.tab;
  }

  get backLink(): { route: string; model?: string } {
    // Use undefined instead of empty string so the array helper can conditionally build @models
    return { route: this.#routeFrom, model: this.id || undefined };
  }

  @action
  setUpcomingVisitsCursor(cursor: string | null): void {
    this.upcomingVisitsIsLoading = true;
    this.upcomingVisitsCursor = cursor;
  }

  @action
  setPreviousVisitsCursor(cursor: string | null): void {
    this.previousVisitsIsLoading = true;
    this.previousVisitsCursor = cursor;
  }

  @action
  setTab(tab: TabValue): void {
    this.tab = tab;
  }

  // Dynamic import extracted so tests can override this to provide a fake
  // component without relying on webpack's module-federation chunk loader
  // (which doesn't resolve in the ember test env). Return type is sourced
  // from the `.d.ts` shim at types/reactRichComponents/visitors/profiles/.
  async loadProfileMatchingRuleModal(): Promise<typeof ProfileMatchingRuleModal> {
    const module = await import('reactRichComponents/visitors/profiles/ProfileMatchingRuleModal');
    return module.ProfileMatchingRuleModal;
  }

  @action
  async editRules(): Promise<void> {
    // Defense-in-depth: template gates the button on the flag, but guard the
    // action too in case it's invoked from devtools, stale DOM, or a test.
    if (!this.canEditRules) return;
    // Ignore re-clicks while the federated chunk is still loading.
    if (this.isLoadingRulesEditor) return;

    const currentRules = this.model.visitorProfile.matchingRules;
    this.isLoadingRulesEditor = true;
    this.rerenderReactProfileShowIfMounted();

    try {
      const ProfileMatchingRuleModal = await this.loadProfileMatchingRuleModal();

      if (this.isDestroying || this.isDestroyed) return;

      this.closeRulesModal();

      this.modalRoot = await this.reactRenderer.render(ProfileMatchingRuleModal, 'react__matching-rule-modal', {
        mode: 'profile' as const,
        companyId: this.state.currentCompany.id,
        initialRule: currentRules ?? undefined,
        isOpen: true,
        profileName: this.model.visitorProfile.displayName,
        onSave: (rule: MatchingRules) => void this.saveMatchingRules(rule),
        onClose: () => this.closeRulesModal(),
        onResetToDefault: (companyRules: MatchingRules) => void this.resetToDefaultRules(companyRules),
      });
    } catch {
      if (this.isDestroying || this.isDestroyed) return;
      this.flashMessages.showAndHideFlash('error', 'Failed to load matching rules editor');
      return;
    } finally {
      if (!this.isDestroying && !this.isDestroyed) {
        this.isLoadingRulesEditor = false;
        this.rerenderReactProfileShowIfMounted();
      }
    }

    if (this.isDestroying || this.isDestroyed) return;
    // Track outside the try/catch so a tracking failure cannot trigger the
    // load-error flash on a successfully rendered modal.
    this.#safeTrack('Profile Matching Rule Modal Opened', {
      source: 'profile',
      is_default: !currentRules,
      has_custom_rule: !!currentRules,
      group_count: currentRules?.v1?.or?.length ?? 0,
    });
  }

  // PATCH only the matching-rules attribute (backend rejects photo-url on
  // write). The response is pushed back into the store so the snapshot stays
  // in sync and rollbackAttributes() doesn't revert to the pre-PATCH value.
  async #patchMatchingRules(rule: MatchingRules): Promise<void> {
    const profileId = this.model.visitorProfile.id;
    const response = await this.ajaxFetch.patch<{
      data: { id: string; type: string; attributes: Record<string, unknown> };
    }>(`/a/visitors/api/v3/visitor-profiles/${profileId}`, {
      contentType: 'application/vnd.api+json',
      headers: { Accept: 'application/vnd.api+json' },
      data: JSON.stringify({
        data: {
          id: profileId,
          type: 'visitor-profiles',
          attributes: { 'matching-rules': rule },
        },
      }),
    });
    this.store.push(this.store.normalize('visitor-profile', response.data));
  }

  // Tracking failures must never break the UX flow.
  #safeTrack(event: string, properties: AnalyticsTraits): void {
    try {
      this.metrics.trackEvent(event, properties);
    } catch {
      /* swallow */
    }
  }

  async saveMatchingRules(rule: MatchingRules): Promise<void> {
    // Re-entry guard: prevents a double-click on the React Save button from
    // firing concurrent PATCHes while the first is still in flight.
    if (this.isSavingRules) return;
    this.isSavingRules = true;
    try {
      try {
        await this.#patchMatchingRules(rule);
      } catch {
        if (this.isDestroying || this.isDestroyed) return;
        this.flashMessages.showAndHideFlash('error', 'Failed to save matching rules');
        return;
      }
      if (this.isDestroying || this.isDestroyed) return;
      const displayName = this.model.visitorProfile.displayName;
      // closeRulesModal() swallows unmount errors internally, so this
      // sequential flow cannot trigger a false-success flash on cleanup failure.
      this.closeRulesModal();
      this.flashMessages.showAndHideFlash('success', `Custom rule saved for ${displayName}`);
      this.#safeTrack('Profile Matching Rule Saved', {
        source: 'profile',
        group_count: rule?.v1?.or?.length ?? 0,
        field_count: rule?.v1?.or?.reduce((sum, group) => sum + (group.and?.length ?? 0), 0) ?? 0,
      });
    } finally {
      if (!this.isDestroying && !this.isDestroyed) {
        this.isSavingRules = false;
      }
    }
  }

  async resetToDefaultRules(companyRules: MatchingRules): Promise<void> {
    // Shares isSavingRules with saveMatchingRules so the two paths cannot race.
    if (this.isSavingRules) return;
    this.isSavingRules = true;
    try {
      try {
        await this.#patchMatchingRules(companyRules);
      } catch {
        if (this.isDestroying || this.isDestroyed) return;
        this.flashMessages.showAndHideFlash('error', 'Failed to update matching rules');
        return;
      }
      if (this.isDestroying || this.isDestroyed) return;
      this.closeRulesModal();
      this.flashMessages.showAndHideFlash('success', 'Rule updated to company default');
      this.#safeTrack('Profile Matching Rule Reset to Default', {
        source: 'profile',
        group_count: companyRules?.v1?.or?.length ?? 0,
      });
    } finally {
      if (!this.isDestroying && !this.isDestroyed) {
        this.isSavingRules = false;
      }
    }
  }

  closeRulesModal(): void {
    if (this.modalRoot) {
      // Unmount errors (e.g. React root already torn down) must not leak
      // into the save path — the modal is going away regardless.
      try {
        this.reactRenderer.unmount(this.modalRoot);
      } catch {
        /* swallow */
      }
      this.modalRoot = undefined;
    }
  }

  // ─── React show-page mount (gated by `vis-visitor-profile-show-rrc`) ──────

  get useReactShowPage(): boolean {
    return this.featureFlags.isEnabled('vis-visitor-profile-show-rrc');
  }

  // Extracted for test override.
  async loadVisitorProfileShow(): Promise<typeof VisitorProfileShow> {
    const module = await import('reactRichComponents/visitors/profiles/VisitorProfileShow');
    return module.VisitorProfileShow;
  }

  // Centralized so mount + rerender stay in sync as props are added.
  #buildReactProfileShowProps() {
    return {
      profileId: this.model.visitorProfile.id,
      initialTab: this.activeTab,
      // Arrow wrap so eslint's unbound-method check passes — same pattern as
      // the matching-rule callbacks (onSave/onClose/onResetToDefault) below.
      onNavigateBack: () => this.handleBackNavigation(),
      canReadProfileVisits: this.abilities.can('read profile visits'),
      canAccessDocumentUploads: this.canAccessDocumentUploads,
      canAccessProfileNotes: this.canAccessProfileNotes,
      canManageNote: this.abilities.can('manage-note visitor-profile'),
      canEditRules: this.canEditRules,
      isLoadingRulesEditor: this.isLoadingRulesEditor,
      // editRules returns Promise<void>; wrap to satisfy no-misused-promises.
      onEditRules: () => void this.editRules(),
    };
  }

  @action
  async mountReactProfileShow(_element: HTMLElement): Promise<void> {
    if (!this.useReactShowPage) return;
    if (this.isDestroying || this.isDestroyed) return;

    try {
      this.reactProfileShowComponent = await this.loadVisitorProfileShow();
      if (this.isDestroying || this.isDestroyed) return;

      this.reactProfileShowRoot = await this.reactRenderer.render(
        this.reactProfileShowComponent,
        'react__visitor-profile-show',
        this.#buildReactProfileShowProps(),
      );
    } catch (err) {
      if (this.isDestroying || this.isDestroyed) return;
      // Surface Module Federation chunk-load failures so they're not silent.
      this.#safeTrack('Visitor Profile Show Mount Failed', {
        profile_id: this.model.visitorProfile.id,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  @action
  unmountReactProfileShow(_element: HTMLElement): void {
    if (this.reactProfileShowRoot) {
      try {
        this.reactRenderer.unmount(this.reactProfileShowRoot);
      } catch {
        /* root may have torn itself down already */
      }
      this.reactProfileShowRoot = undefined;
      this.reactProfileShowComponent = undefined;
    }
  }

  // Push the new model id into the existing React root via `rerender` —
  // avoids the createRoot race that an unmount+remount cycle would have.
  @action
  remountReactProfileShow(element: HTMLElement, [_profileId]: [string]): void {
    if (this.isDestroying || this.isDestroyed) return;
    if (!this.reactProfileShowRoot || !this.reactProfileShowComponent) {
      // Initial mount hasn't finished (or failed). Let did-insert handle it.
      void this.mountReactProfileShow(element);
      return;
    }
    this.reactRenderer.rerender(
      this.reactProfileShowComponent,
      this.reactProfileShowRoot,
      this.#buildReactProfileShowProps(),
    );
  }

  // Call after flipping a tracked prop the React side reads (e.g. editRules
  // toggling isLoadingRulesEditor) so the change propagates without remount.
  rerenderReactProfileShowIfMounted(): void {
    if (!this.reactProfileShowRoot || !this.reactProfileShowComponent) return;
    if (this.isDestroying || this.isDestroyed) return;
    this.reactRenderer.rerender(
      this.reactProfileShowComponent,
      this.reactProfileShowRoot,
      this.#buildReactProfileShowProps(),
    );
  }

  @action
  handleBackNavigation(): void {
    if (this.id) {
      void this.router.transitionTo(this.#routeFrom, this.id);
    } else {
      void this.router.transitionTo(this.#routeFrom);
    }
  }

  get #routeFrom(): string {
    switch (this.from) {
      case 'entry':
        return 'visitors.entry';
      case 'invite':
        return 'visitors.invites.show';
      default:
        return 'visitors.profiles';
    }
  }
}
